import { useEffect, useMemo, useState } from "react";
import { NANAE_CADRES } from "@/components/qv3/NanaeShared";
import { LLM_MODELS, type LLMPersonaId } from "@/components/brand/llm-logos";

/**
 * CouncilCircle — Les 7 sages disposés en cercle.
 * Animation : des liens se tracent entre paires aléatoires.
 *   - Blanc  = accord
 *   - Rouge  = désaccord
 * Pure CSS/SVG, pas de lib d'anim.
 */
type Edge = { id: number; a: number; b: number; agree: boolean };

const SAGES = NANAE_CADRES.filter((c) => c.id !== "pazuru").slice(0, 7);

export function CouncilCircle({ size = 460 }: { size?: number }) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.38;
  const portrait = Math.max(56, size * 0.14);

  // Position de chaque sage sur le cercle (commence en haut, sens horaire)
  const positions = useMemo(() => {
    return SAGES.map((_, i) => {
      const angle = (i / SAGES.length) * Math.PI * 2 - Math.PI / 2;
      return { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius };
    });
  }, [cx, cy, radius]);

  const [edges, setEdges] = useState<Edge[]>([]);
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    let mounted = true;
    let counter = 0;
    const tick = () => {
      if (!mounted) return;
      const a = Math.floor(Math.random() * SAGES.length);
      let b = Math.floor(Math.random() * SAGES.length);
      if (b === a) b = (b + 1) % SAGES.length;
      const agree = Math.random() > 0.32; // un peu plus d'accords que de désaccords
      const id = ++counter;
      setEdges((prev) => [...prev.slice(-5), { id, a, b, agree }]);
      setSeq((s) => s + 1);
    };
    tick();
    const it = setInterval(tick, 1400);
    return () => {
      mounted = false;
      clearInterval(it);
    };
  }, []);

  // purge des arêtes anciennes
  useEffect(() => {
    if (edges.length === 0) return;
    const t = setTimeout(() => {
      setEdges((prev) => prev.filter((e) => e.id > (edges[0]?.id ?? 0) - 6));
    }, 2400);
    return () => clearTimeout(t);
  }, [seq, edges]);

  return (
    <div
      className="nanae-council-circle"
      style={{ position: "relative", width: size, height: size, margin: "0 auto" }}
      aria-label="Le Conseil — sept sages en cercle"
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        aria-hidden="true"
      >
        {/* anneau guide très discret */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="rgba(255,23,68,0.18)"
          strokeWidth={0.5}
          strokeDasharray="2 6"
        />
        {edges.map((e) => {
          const p1 = positions[e.a];
          const p2 = positions[e.b];
          const stroke = e.agree ? "rgba(255,255,255,0.85)" : "rgba(196,52,52,0.9)";
          const glow = e.agree ? "rgba(255,255,255,0.35)" : "rgba(196,52,52,0.45)";
          return (
            <g key={e.id} style={{ animation: "ncc-edge-life 2.4s ease-out forwards" }}>
              <line
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke={glow}
                strokeWidth={3}
                strokeLinecap="round"
                opacity={0.6}
              />
              <line
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke={stroke}
                strokeWidth={1}
                strokeLinecap="round"
                className="ncc-edge-draw"
                style={{
                  strokeDasharray: 600,
                  strokeDashoffset: 600,
                  animation: "ncc-draw 0.9s ease-out forwards",
                }}
              />
              {/* pulses aux extrémités */}
              <circle cx={p1.x} cy={p1.y} r={2.5} fill={stroke} />
              <circle cx={p2.x} cy={p2.y} r={2.5} fill={stroke} />
            </g>
          );
        })}
      </svg>

      {SAGES.map((c, i) => {
        const p = positions[i];
        return (
          <figure
            key={c.id}
            className="nanae-council-circle-node"
            style={{
              position: "absolute",
              width: Math.max(portrait, 120),
              left: p.x - Math.max(portrait, 120) / 2,
              top: p.y - portrait / 2,
              margin: 0,
              textAlign: "center",
            }}
            title={`${c.name} — ${c.role ?? ""}`}
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                aria-hidden="true"
                style={{
                  width: portrait,
                  height: portrait,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "0.5px solid rgba(255,23,68,0.35)",
                  background: "#111",
                }}
              >
                {c.imageUrl ? (
                  <img
                    src={c.imageUrl}
                    alt=""
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(1)" }}
                  />
                ) : null}
              </div>
            </div>
            <figcaption
              style={{
                marginTop: 8,
                fontFamily: '"Playfair Display", serif',
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--qv3-ivory, #efe7d6)",
                lineHeight: 1.1,
              }}
            >
              {c.name}
              <div className="qv3-mono-hud size-xs is-caps nanae-council-meta" style={{ marginTop: 4 }}>
                {c.role}
              </div>
              {LLM_MODELS[c.id as LLMPersonaId] && (
                <div
                  className="qv3-mono-hud size-xs is-caps nanae-council-meta"
                  style={{ marginTop: 3, color: "#F5EFE3", fontWeight: 700 }}
                >
                  {LLM_MODELS[c.id as LLMPersonaId].provider.toUpperCase()}
                </div>
              )}
            </figcaption>
          </figure>

        );
      })}

      <p
        className="qv3-mono-hud size-xs is-caps"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          transform: "translateY(-50%)",
          textAlign: "center",
          margin: 0,
          letterSpacing: "0.2em",
          opacity: 0.8,
        }}
      >
        7 SAGES
        <br />
        PRÊTS À DÉLIBÉRER
      </p>

      <style>{`
        @keyframes ncc-draw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes ncc-edge-life {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          75%  { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
